import { useForm } from "react-hook-form";

import AssessmentApi from "@/api/assessment-api";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import { handled, logRejection } from "@/helpers";
import useSnackbar from "@/hooks/snackbar";
import type { Assessor } from "@/types/assessment";

interface AssessorFormValues {
  email: string;
}

// toggleFn arrives from the surrounding Modal through cloneElement, never from the call site.
interface AssessorFormProps {
  assignment_id: string;
  assessment_step_id: string;
  callbackFn?: (assessor: Assessor | null) => void;
  toggleFn?: () => void;
}

export default function AssessorForm({
  assignment_id,
  assessment_step_id,
  callbackFn,
  toggleFn,
}: AssessorFormProps) {
  const { showSnackbar } = useSnackbar();
  const { register, handleSubmit } = useForm<AssessorFormValues>();

  const _handleSubmit = (formdata: AssessorFormValues) => {
    const abort_controller = new AbortController();
    const { email } = formdata;
    AssessmentApi.addAssessmentUser(
      email,
      assignment_id,
      assessment_step_id,
      abort_controller.signal,
    )
      .then(({ error, data }) => {
        if (error) {
          if (error.status === 409) {
            showSnackbar(`Assessor ${email} already added!`, "warning");
          } else {
            console.error(error);
            // The modal stays open so the address is still there to retry with.
            showSnackbar(`Could not add ${email} as an assessor.`, "warning");
            return;
          }
        } else {
          if (callbackFn) callbackFn(data);
          showSnackbar(`Assessor ${email} added!`, "success");
        }
        toggleFn?.();
      })
      .catch(logRejection);
  };

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    toggleFn?.();
  };

  return (
    <form
      onSubmit={handled(handleSubmit(_handleSubmit))}
      className="mt-4 w-[40rem] rounded-lg bg-slate-800 p-8"
    >
      <Input label="E-mail address" id="email" register={register} />

      <div className="flex items-center gap-4">
        <Button type="submit">Submit</Button>
        <Button onClick={_handleCancel}>Cancel</Button>
      </div>
    </form>
  );
}
